import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Search, Briefcase, FileText, Users, Building2, Star, ArrowRight, CheckCircle2, Sparkles, Shield, Zap, Quote, DollarSign, BookOpen, Bell, Send, MapPin, Loader2, Monitor, EyeOff, Clock, TrendingUp, Headphones } from 'lucide-react';
import axios from 'axios';
import { formatPayDisplay } from '../utils/payDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://images.unsplash.com/photo-1628645288866-908f2c43da39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxibGFjayUyMHdvbWFuJTIwd29ya2luZyUyMGZyb20lMjBob21lJTIwbGFwdG9wJTIwc21pbGluZ3xlbnwwfHx8fDE3NzYwMjk4MzV8MA&ixlib=rb-4.1.0&q=85&w=1200";
const WFH_IMG_2 = "https://images.unsplash.com/photo-1758874573279-2709f2ce5d73?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxoaXNwYW5pYyUyMG1hbiUyMHJlbW90ZSUyMHdvcmslMjBob21lJTIwb2ZmaWNlJTIwZGVza3xlbnwwfHx8fDE3NzYwMjk4MzV8MA&ixlib=rb-4.1.0&q=85&w=600";
const WFH_IMG_3 = "https://images.unsplash.com/photo-1675004865271-60ca34042c89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxhc2lhbiUyMHdvbWFuJTIwd29ya2luZyUyMGxhcHRvcCUyMGhvbWUlMjBjb3p5fGVufDB8fHx8MTc3NjAyOTgzNXww&ixlib=rb-4.1.0&q=85&w=600";
const WFH_IMG_4 = "https://images.unsplash.com/photo-1610473068514-276d33c606dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxibGFjayUyMHdvbWFuJTIwd29ya2luZyUyMGZyb20lMjBob21lJTIwbGFwdG9wJTIwc21pbGluZ3xlbnwwfHx8fDE3NzYwMjk4MzV8MA&ixlib=rb-4.1.0&q=85&w=600";
const WFH_IMG_5 = "https://images.unsplash.com/photo-1758598497414-3ddd779e2611?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxoaXNwYW5pYyUyMG1hbiUyMHJlbW90ZSUyMHdvcmslMjBob21lJTIwb2ZmaWNlJTIwZGVza3xlbnwwfHx8fDE3NzYwMjk4MzV8MA&ixlib=rb-4.1.0&q=85&w=600";

const QUOTES = [
  "Your dream remote job is out there. Every application brings you closer.",
  "You are one application away from changing your entire life.",
  "Remote work gives you freedom. Freedom gives you time. Time gives you life.",
  "Today is your day to start fresh, apply boldly, and believe in your worth."
];

const FEATURES = [
  { icon: Search, title: "Smart Job Search", desc: "Filter by category, state, equipment provided & more" },
  { icon: Zap, title: "Quick Apply", desc: "One-click apply with your saved profile" },
  { icon: FileText, title: "AI Resume Builder", desc: "Build pro resumes with GPT, Claude, or Gemini" },
  { icon: Users, title: "Community Groups", desc: "Network with fellow remote workers" },
  { icon: Building2, title: "Business Portal", desc: "Companies post verified WFH positions" },
  { icon: Shield, title: "Application Tracker", desc: "Track every application in one place" },
];

const CATEGORIES = [
  { name: "Customer Service", count: "1,200+", color: "bg-[#A7F3D0]/30" },
  { name: "Tech & Development", count: "890+", color: "bg-[#D97757]/10" },
  { name: "Data Entry", count: "650+", color: "bg-[#86A789]/20" },
  { name: "Healthcare", count: "430+", color: "bg-[#A7F3D0]/30" },
  { name: "Sales & Marketing", count: "780+", color: "bg-[#D97757]/10" },
  { name: "Education", count: "340+", color: "bg-[#86A789]/20" },
];

export default function LandingPage() {
  const { user } = useAuth();
  const [quoteIdx, setQuoteIdx] = useState(0);
  const [showPostForm, setShowPostForm] = useState(false);
  const [postForm, setPostForm] = useState({ title: '', pay: '', location: 'Remote', description: '', contact_email: '' });
  const [posting, setPosting] = useState(false);
  const [postSuccess, setPostSuccess] = useState(false);
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [homepageData, setHomepageData] = useState(null);
  const [activeCategory, setActiveCategory] = useState('top');

  useEffect(() => {
    const interval = setInterval(() => setQuoteIdx(i => (i + 1) % QUOTES.length), 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    axios.get(`${API}/jobs/featured-upfront`).then(r => setFeaturedJobs(r.data.jobs || [])).catch(() => {});
    axios.get(`${API}/jobs/homepage`).then(r => setHomepageData(r.data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    if (params.get('posted') === 'success') setPostSuccess(true);
  }, []);

  const handlePostJob = async () => {
    if (!postForm.title || !postForm.pay || !postForm.contact_email || !postForm.description) return;
    setPosting(true);
    try {
      const { data } = await axios.post(`${API}/jobs/post-upfront`, postForm);
      const { data: payment } = await axios.post(`${API}/jobs/post-upfront/${data.job_id}/pay`, { origin_url: window.location.origin });
      window.location.href = payment.url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit job');
      setPosting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Nav */}
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center">
              <Briefcase size={14} className="text-white" strokeWidth={1.5} />
            </div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
          <div className="hidden md:flex items-center gap-1">
            <Link to="/jobs" className="text-sm font-medium text-[#1C2A24] hover:text-[#047857] hover:bg-[#A7F3D0]/15 px-3 py-2 rounded-full transition-all" data-testid="nav-find-jobs">Find Jobs</Link>
            <Link to="/platforms" className="text-sm font-medium text-[#1C2A24] hover:text-[#047857] hover:bg-[#A7F3D0]/15 px-3 py-2 rounded-full transition-all" data-testid="nav-platforms">WFH Platforms</Link>
            <Link to="/hiring-events" className="text-sm font-medium text-[#1C2A24] hover:text-[#047857] hover:bg-[#A7F3D0]/15 px-3 py-2 rounded-full transition-all" data-testid="nav-events">Hiring Events</Link>
            <Link to="/membership" className="text-sm font-medium text-[#1C2A24] hover:text-[#047857] hover:bg-[#A7F3D0]/15 px-3 py-2 rounded-full transition-all" data-testid="nav-pricing">Pricing</Link>
            <Link to="/equipment" className="text-sm font-medium text-[#1C2A24] hover:text-[#047857] hover:bg-[#A7F3D0]/15 px-3 py-2 rounded-full transition-all" data-testid="nav-equipment">Equipment</Link>
            <Link to="/blog" className="text-sm font-medium text-[#1C2A24] hover:text-[#047857] hover:bg-[#A7F3D0]/15 px-3 py-2 rounded-full transition-all" data-testid="nav-blog">Blog</Link>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard"><Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-10 px-6" data-testid="go-to-dashboard">Dashboard</Button></Link>
            ) : (
              <>
                <Link to="/auth"><Button variant="ghost" className="rounded-full h-10 text-[#1C2A24]" data-testid="nav-login">Sign In</Button></Link>
                <Link to="/auth"><Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-10 px-6" data-testid="nav-signup">Get Started</Button></Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#A7F3D0]/30 text-[#047857] text-sm font-medium mb-6">
                <Shield size={14} strokeWidth={1.5} /> Verified & Scam-Free Listings
              </div>
              <h1 className="text-5xl md:text-6xl tracking-tight font-light leading-[1.1] mb-6" style={{fontFamily:'Outfit'}}>
                Find <span className="font-semibold text-[#047857]">REAL Remote</span> Jobs — No Scams, No Wasted Time
              </h1>
              <p className="text-base leading-relaxed text-[#1C2A24]/70 mb-2 max-w-lg" style={{fontFamily:'Manrope'}}>
                Access verified remote jobs, apply faster, and see pay clearly.
              </p>
              <p className="text-sm text-[#1C2A24]/50 mb-8 max-w-lg" style={{fontFamily:'Manrope'}}>
                Join job seekers finding real remote jobs faster with WorkFromJoy.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/jobs">
                  <Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-12 px-8 text-base" data-testid="hero-find-jobs">
                    Find Remote Jobs Now <ArrowRight size={16} className="ml-2" strokeWidth={1.5} />
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button variant="outline" className="rounded-full border-[#E5E0D8] text-[#1C2A24] h-12 px-8 text-base hover:bg-[#F4F1E8]" data-testid="hero-signup">
                    Create Free Account
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-6 mt-8 text-sm text-[#1C2A24]/70">
                <span className="flex items-center gap-1"><CheckCircle2 size={14} className="text-[#047857]" strokeWidth={1.5} /> Updated daily</span>
                <span className="flex items-center gap-1"><CheckCircle2 size={14} className="text-[#047857]" strokeWidth={1.5} /> Verified sources</span>
                <span className="flex items-center gap-1"><CheckCircle2 size={14} className="text-[#047857]" strokeWidth={1.5} /> No scams or fake listings</span>
              </div>
            </div>
            <div className="relative animate-fade-up stagger-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-3xl overflow-hidden shadow-[0_20px_60px_rgb(0,0,0,0.1)] row-span-2">
                  <img src={HERO_IMG} alt="Woman working from home" className="w-full h-full min-h-[400px] object-cover" />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-[0_10px_30px_rgb(0,0,0,0.08)]">
                  <img src={WFH_IMG_2} alt="Man at home office" className="w-full h-[192px] object-cover" />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-[0_10px_30px_rgb(0,0,0,0.08)]">
                  <img src={WFH_IMG_3} alt="Remote worker at home" className="w-full h-[192px] object-cover" />
                </div>
              </div>
              {/* Floating quote card */}
              <div className="absolute -bottom-6 -left-6 right-12 quote-card rounded-2xl p-5 shadow-lg border border-[#E5E0D8]" data-testid="motivational-quote-card">
                <p className="text-sm italic text-[#1C2A24]/80 leading-relaxed" style={{fontFamily:'Outfit'}}>"{QUOTES[quoteIdx]}"</p>
                <p className="text-xs text-[#047857] mt-2 font-medium">Daily Motivation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Employer Quick Teaser */}
      <section className="py-6 bg-[#1C2A24]" data-testid="employer-teaser">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#047857] flex items-center justify-center shrink-0">
                <Building2 size={18} className="text-white" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-white text-sm font-medium" style={{fontFamily:'Outfit'}}>Hiring remotely? Post WFH jobs & host live hiring events</p>
                <p className="text-white/50 text-xs">Business plans from $99/mo — Post jobs, screen candidates, hire faster</p>
              </div>
            </div>
            <div className="flex gap-3 shrink-0">
              <Link to="/auth">
                <Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-9 px-5 text-sm" data-testid="teaser-employer-signup">Create Business Account</Button>
              </Link>
              <Link to="/membership">
                <Button variant="outline" className="rounded-full border-white/20 text-white h-9 px-5 text-sm hover:bg-white/10" data-testid="teaser-employer-plans">View Plans</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ LIVE JOBS PREVIEW ═══ */}
      {homepageData && (
        <section className="py-16 md:py-20" data-testid="live-jobs-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            {/* Section header with stats */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 rounded-full bg-[#047857] animate-pulse" />
                  <span className="text-xs font-medium text-[#047857] uppercase tracking-wider">Live on WorkFromJoy</span>
                </div>
                <h2 className="text-3xl md:text-4xl tracking-tight font-medium" style={{fontFamily:'Outfit'}}>Real Jobs, Right Now</h2>
                <p className="text-base text-[#1C2A24]/60 mt-2">Browse {homepageData.stats.total_jobs}+ verified remote positions. {homepageData.stats.jobs_with_pay} jobs with pay clearly listed.</p>
              </div>
              <Link to="/jobs">
                <Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-11 px-6" data-testid="view-all-jobs-top">
                  View All Jobs <ArrowRight size={14} className="ml-2" strokeWidth={1.5} />
                </Button>
              </Link>
            </div>

            {/* Category quick tabs */}
            <div className="flex flex-wrap gap-2 mb-8" data-testid="homepage-category-tabs">
              {[
                { key: 'top', label: 'Best Match', icon: Star },
                { key: 'hiring_now', label: 'Hiring Now', icon: TrendingUp },
                { key: 'entry_level', label: 'Entry-Level Remote', icon: Headphones },
                { key: 'pay_listed', label: 'Jobs With Pay Listed', icon: DollarSign },
              ].map(tab => (
                <button key={tab.key} onClick={() => setActiveCategory(tab.key)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                    activeCategory === tab.key
                      ? 'bg-[#1C2A24] text-white shadow-[0_4px_12px_rgb(28,42,36,0.2)]'
                      : 'bg-white text-[#1C2A24]/70 border border-[#E5E0D8] hover:border-[#047857]/30 hover:bg-[#047857]/5'
                  }`}
                  data-testid={`tab-${tab.key}`}
                >
                  <tab.icon size={14} strokeWidth={1.5} />{tab.label}
                </button>
              ))}
            </div>

            {/* Job cards grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="homepage-job-grid">
              {(activeCategory === 'top' ? homepageData.top_jobs :
                activeCategory === 'hiring_now' ? homepageData.hiring_now :
                activeCategory === 'entry_level' ? homepageData.entry_level :
                homepageData.pay_listed
              ).slice(0, 6).map(job => {
                const pay = formatPayDisplay(job.salary_range);
                const isHighValue = pay.isDisclosed && (job.quick_apply || ['customer-service','chat-support','data-entry','admin'].includes(job.category));
                const hoursAgo = job.created_at ? (Date.now() - new Date(job.created_at).getTime()) / 3600000 : 9999;
                const isRecent = hoursAgo < 48;
                const isActivelyHiring = (job.applicants || 0) >= 5;
                return (
                  <Link key={job.id} to={`/jobs/${job.id}`} className="group" data-testid={`homepage-job-${job.id}`}>
                    <div className={`bg-white rounded-3xl border p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(4,120,87,0.08)] transition-all duration-300 h-full relative ${isHighValue ? 'border-[#047857]/25 ring-1 ring-[#047857]/[0.07]' : 'border-[#E5E0D8]'}`}>
                      {/* Urgency label — only one, most relevant */}
                      {isRecent ? (
                        <span className="absolute top-4 right-4 text-[10px] px-2 py-0.5 rounded-full bg-[#047857]/10 text-[#047857] font-medium" data-testid="label-recent">Recently posted</span>
                      ) : isActivelyHiring ? (
                        <span className="absolute top-4 right-4 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium" data-testid="label-active">Actively hiring</span>
                      ) : job.quick_apply ? (
                        <span className="absolute top-4 right-4 text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium" data-testid="label-quick">Quick apply available</span>
                      ) : null}

                      <div className="flex items-start justify-between mb-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isHighValue ? 'bg-[#A7F3D0]/30' : 'bg-[#F4F1E8]'}`}>
                          <Briefcase size={18} className="text-[#047857]" strokeWidth={1.5} />
                        </div>
                        <div className="flex gap-1.5 flex-wrap justify-end max-w-[55%]">
                          {job.verified && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#A7F3D0]/30 text-[#047857] font-medium flex items-center gap-0.5"><CheckCircle2 size={9} />Verified</span>}
                          {job.featured && <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium flex items-center gap-0.5"><Star size={9} />Featured</span>}
                          {job.equipment_provided && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F4F1E8] text-[#1C2A24]/60 font-medium flex items-center gap-0.5"><Monitor size={9} />Equip.</span>}
                        </div>
                      </div>
                      <h3 className="font-medium text-base mb-1 line-clamp-1 group-hover:text-[#047857] transition-colors" style={{fontFamily:'Outfit'}}>{job.title}</h3>
                      <p className="text-sm text-[#1C2A24]/60 mb-2">{job.company}</p>
                      <div className="flex items-center gap-3 text-xs text-[#1C2A24]/50 mb-3">
                        <span className="flex items-center gap-1"><MapPin size={12} strokeWidth={1.5} />{job.state}</span>
                        <span>{job.job_type}</span>
                      </div>
                      <div className="flex items-end justify-between">
                        <div>
                          {pay.isDisclosed
                            ? <p className="text-sm font-medium text-[#047857]" data-testid="hp-job-pay-disclosed">{pay.text}</p>
                            : <p className="text-xs text-[#1C2A24]/40 flex items-center gap-1" data-testid="hp-job-pay-undisclosed"><EyeOff size={11} strokeWidth={1.5} />{pay.text}</p>
                          }
                        </div>
                        <span className="text-xs text-[#047857] font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1" data-testid="view-job-cue">
                          View Job <ArrowRight size={12} strokeWidth={2} />
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Urgency micro-copy */}
            <p className="text-center text-sm text-[#1C2A24]/50 mt-6" data-testid="urgency-microcopy">
              These jobs are actively hiring — apply before they fill
            </p>

            {/* Browse More CTA */}
            <div className="text-center mt-10">
              <Link to="/jobs">
                <Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-12 px-10 text-base" data-testid="browse-more-jobs">
                  Browse All {homepageData.stats.total_jobs}+ Jobs <ArrowRight size={16} className="ml-2" strokeWidth={1.5} />
                </Button>
              </Link>
              <div className="flex items-center justify-center gap-6 mt-5 text-xs text-[#1C2A24]/40">
                <span className="flex items-center gap-1"><Clock size={12} strokeWidth={1.5} /> Updated daily</span>
                <span className="flex items-center gap-1"><Shield size={12} strokeWidth={1.5} /> Verified sources</span>
                <span className="flex items-center gap-1"><CheckCircle2 size={12} strokeWidth={1.5} /> No scams or fake listings</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Why This Is Different */}
      <section className="py-20 md:py-24" data-testid="why-different-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl tracking-tight font-medium mb-4" style={{fontFamily:'Outfit'}}>Tired of Fake Work-From-Home Jobs?</h2>
          <p className="text-base text-[#1C2A24]/70 max-w-2xl mx-auto leading-relaxed">Most job sites are filled with scams, outdated posts, and fake offers. WorkFromJoy helps you skip the nonsense and go straight to real opportunities.</p>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-[#F4F1E8]/50 to-[#E8F5E9]/30" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl tracking-tight font-medium mb-4" style={{fontFamily:'Outfit'}}>Everything You Need — In One Place</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: CheckCircle2, title: "Real Remote Job Listings", desc: "Verified positions from real companies — no fake offers, no bait-and-switch" },
              { icon: Shield, title: "Scam-Filtered Opportunities", desc: "Every listing is screened. We remove suspicious posts so you don't have to" },
              { icon: Search, title: "Easy Browsing & Search", desc: "Filter by category, state, equipment provided, and more — find what fits you" },
              { icon: Zap, title: "Updated Daily", desc: "Fresh jobs added every day from verified employers and trusted job boards" },
              { icon: Star, title: "No Signup Required to Start", desc: "Browse all jobs for free. Create an account when you're ready to apply" },
              { icon: Sparkles, title: "AI-Powered Tools", desc: "Resume builder, practice interviews, typing tests — everything to help you land the job" },
            ].map((f, i) => (
              <div key={i} className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(4,120,87,0.08)] transition-all duration-300">
                <div className="w-12 h-12 rounded-2xl bg-[#A7F3D0]/30 flex items-center justify-center mb-5">
                  <f.icon size={22} className="text-[#047857]" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-medium mb-2" style={{fontFamily:'Outfit'}}>{f.title}</h3>
                <p className="text-sm text-[#1C2A24]/60 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-4xl tracking-tight font-medium mb-12 text-center" style={{fontFamily:'Outfit'}}>How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: "1", title: 'Click "Find Remote Jobs"', desc: "Head to the job board and start browsing — no signup needed" },
              { step: "2", title: "Browse Verified Listings", desc: "Filter by category, state, pay, and equipment provided" },
              { step: "3", title: "Apply With Confidence", desc: "Every listing is screened. Apply knowing it's a real opportunity" },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-[#047857] flex items-center justify-center mx-auto mb-5 text-white text-xl font-semibold" style={{fontFamily:'Outfit'}}>{s.step}</div>
                <h3 className="text-lg font-medium mb-2" style={{fontFamily:'Outfit'}}>{s.title}</h3>
                <p className="text-sm text-[#1C2A24]/60 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview - Moved up for visibility */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-[#F4F1E8]/50 to-[#FFF8F0]/30" data-testid="pricing-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl tracking-tight font-medium mb-4" style={{fontFamily:'Outfit'}}>Affordable Plans for Everyone</h2>
          <p className="text-base text-[#1C2A24]/60 mb-12 max-w-2xl mx-auto">We keep our prices low because we know you're investing in your future. Start free, upgrade when you're ready.</p>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { name: "Free", price: "$0", desc: "Get started", features: ["Browse all jobs", "10 apps/month", "Basic community"] },
              { name: "Plus", price: "$4.99", desc: "/month", features: ["Unlimited applications", "Quick Apply", "Full community", "Resume storage"], highlight: false },
              { name: "Pro", price: "$12.99", desc: "/month", features: ["Everything in Plus", "AI Resume Builder", "Priority visibility", "Assessment prep"], highlight: true },
            ].map((p, i) => (
              <div key={i} className={`bg-white rounded-3xl border p-8 ${p.highlight ? 'pricing-highlight' : 'border-[#E5E0D8]'} shadow-[0_8px_30px_rgb(0,0,0,0.04)]`}>
                {p.highlight && <div className="text-xs font-medium text-[#047857] bg-[#A7F3D0]/30 rounded-full px-3 py-1 inline-block mb-4">Most Popular</div>}
                <h3 className="text-xl font-medium" style={{fontFamily:'Outfit'}}>{p.name}</h3>
                <div className="mt-4 mb-6">
                  <span className="text-4xl font-semibold" style={{fontFamily:'Outfit'}}>{p.price}</span>
                  <span className="text-sm text-[#1C2A24]/60">{p.desc}</span>
                </div>
                <ul className="space-y-3 text-sm text-left mb-8">
                  {p.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#047857] shrink-0" strokeWidth={1.5} />{f}</li>
                  ))}
                </ul>
                <Link to="/membership">
                  <Button className={`w-full rounded-full h-11 ${p.highlight ? 'bg-[#047857] hover:bg-[#065F46] text-white' : 'bg-[#F4F1E8] text-[#1C2A24] hover:bg-[#E5E0D8]'}`}>
                    {p.name === "Free" ? "Start Free" : "Get Started"}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diverse People Strip */}
      <section className="py-12 bg-gradient-to-r from-[#E8F5E9]/40 via-[#FDFBF7] to-[#FFF3E0]/30 overflow-hidden" data-testid="people-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-6 overflow-x-auto pb-2">
            {[HERO_IMG, WFH_IMG_2, WFH_IMG_3, WFH_IMG_4, WFH_IMG_5].map((img, i) => (
              <div key={i} className="shrink-0 w-44 h-44 rounded-3xl overflow-hidden shadow-[0_8px_24px_rgb(0,0,0,0.08)] hover:scale-105 transition-transform duration-300">
                <img src={img} alt={`Remote worker ${i + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
            <div className="shrink-0 w-44 h-44 rounded-3xl bg-[#047857] flex flex-col items-center justify-center text-white p-4">
              <p className="text-2xl font-semibold" style={{fontFamily:'Outfit'}}>10K+</p>
              <p className="text-xs text-white/70 text-center mt-1">Remote workers in our community</p>
            </div>
          </div>
        </div>
      </section>

      {/* For Employers */}
      <section className="py-24 md:py-32 bg-[#1C2A24]" data-testid="employer-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#A7F3D0] text-sm font-medium mb-4 block">For Employers & Companies</span>
              <h2 className="text-3xl md:text-4xl tracking-tight font-medium text-white mb-6" style={{fontFamily:'Outfit'}}>Hire Remote Talent Fast</h2>
              <p className="text-white/60 mb-8 leading-relaxed">LLCs, startups, and enterprise companies — post WFH jobs and connect with qualified candidates instantly. Everything you need to hire remotely, in one platform.</p>
              <div className="space-y-4 mb-8">
                {[
                  { title: "Live Hiring Events", desc: "Host real-time interviews with a waiting room. Candidates join, you admit one at a time — via text chat or Zoom/Google Meet video." },
                  { title: "Post Jobs & Get Applicants", desc: "Post WFH positions with state requirements, equipment details, and Quick Apply. Receive applications with full candidate profiles and resumes." },
                  { title: "Featured Employer Badge", desc: "Stand out with a Featured badge on your listings and hiring events. Priority visibility to job seekers." },
                  { title: "Candidate Filtering & Analytics", desc: "View applicant skills, match scores, and track your hiring pipeline. Growth & Enterprise plans include advanced analytics." },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={16} className="text-[#A7F3D0] mt-1 shrink-0" strokeWidth={1.5} />
                    <div>
                      <p className="text-white text-sm font-medium">{item.title}</p>
                      <p className="text-white/50 text-xs mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Link to="/auth">
                  <Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-11 px-8" data-testid="employer-signup-btn">
                    Create Business Account
                  </Button>
                </Link>
                <Link to="/membership">
                  <Button variant="outline" className="rounded-full border-white/20 text-white h-11 px-8 hover:bg-white/10" data-testid="employer-pricing-btn">
                    View Business Plans
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/5 rounded-3xl p-8 border border-white/10">
                <p className="text-[#A7F3D0] text-xs font-medium mb-4">Business Plans Starting At</p>
                <div className="space-y-4">
                  <div className="bg-white/5 rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white font-medium text-sm">Starter</span>
                      <span className="text-[#A7F3D0] font-semibold" style={{fontFamily:'Outfit'}}>$99/mo</span>
                    </div>
                    <p className="text-white/40 text-xs">3 job posts + Live Chat Hiring Events</p>
                  </div>
                  <div className="bg-white/5 rounded-2xl p-4 border border-[#047857]/30">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white font-medium text-sm">Growth <span className="text-[10px] bg-[#047857] text-white px-2 py-0.5 rounded-full ml-1">Popular</span></span>
                      <span className="text-[#A7F3D0] font-semibold" style={{fontFamily:'Outfit'}}>$249/mo</span>
                    </div>
                    <p className="text-white/40 text-xs">15 posts + Video/Zoom Hiring + Waiting Room</p>
                  </div>
                  <div className="bg-white/5 rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white font-medium text-sm">Enterprise</span>
                      <span className="text-[#A7F3D0] font-semibold" style={{fontFamily:'Outfit'}}>$499/mo</span>
                    </div>
                    <p className="text-white/40 text-xs">Unlimited + Featured Badge + Analytics + API</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-[#FDFBF7] to-[#E8F5E9]/20" data-testid="success-stories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl tracking-tight font-medium mb-4" style={{fontFamily:'Outfit'}}>Real People, Real WFH Success</h2>
            <p className="text-base text-[#1C2A24]/60 max-w-2xl mx-auto">Hear from remote workers who found their dream jobs and changed their lives.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Maria R.", role: "Customer Service Rep", company: "Remote Health Co.", story: "I was a single mom working retail. WorkFromJoy helped me find a fully remote customer service job that pays $38K with benefits. I'm home with my kids every day now.", rating: 5, since: "WFH since Jan 2025", img: WFH_IMG_4, initials: "MR", color: "bg-[#D97757]" },
              { name: "James T.", role: "Data Entry Specialist", company: "DataFlow Corp", story: "After getting laid off, I was scared. I signed up for the Pro plan, used the AI Resume Builder, and landed a data entry job within 2 weeks. The typing test prep was a game changer.", rating: 5, since: "WFH since Mar 2025", img: WFH_IMG_5, initials: "JT", color: "bg-[#047857]" },
              { name: "Ashley K.", role: "Virtual Assistant", company: "ExecSupport Pro", story: "I went from making $12/hr in food service to $48K/year working from home as a VA. The community group for VAs gave me tips that helped me nail my interview.", rating: 5, since: "WFH since Nov 2024", img: WFH_IMG_3, initials: "AK", color: "bg-[#86A789]" },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(4,120,87,0.08)] transition-all duration-300" data-testid={`success-story-${i}`}>
                <Quote size={20} className="text-[#047857]/30 mb-4" strokeWidth={1.5} />
                <p className="text-sm text-[#1C2A24]/70 leading-relaxed mb-6 italic">"{s.story}"</p>
                <div className="border-t border-[#E5E0D8] pt-4">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({length: s.rating}).map((_, j) => (
                      <Star key={j} size={12} className="text-[#D97757] fill-[#D97757]" strokeWidth={1.5} />
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${s.color} flex items-center justify-center text-white text-xs font-semibold shrink-0`}>{s.initials}</div>
                    <div>
                      <p className="text-sm font-medium" style={{fontFamily:'Outfit'}}>{s.name}</p>
                      <p className="text-xs text-[#047857]">{s.role} at {s.company}</p>
                      <p className="text-xs text-[#1C2A24]/40">{s.since}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Resources & Tools Strip */}
      <section className="py-16 bg-gradient-to-r from-[#F4F1E8]/60 via-[#E8F5E9]/30 to-[#FFF3E0]/20" data-testid="resources-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/blog" className="group flex items-start gap-4 bg-white rounded-2xl border border-[#E5E0D8] p-6 hover:border-[#047857]/30 transition-all" data-testid="resources-blog-link">
              <div className="w-10 h-10 rounded-xl bg-[#047857]/10 flex items-center justify-center shrink-0">
                <BookOpen size={18} className="text-[#047857]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-sm font-medium mb-1" style={{fontFamily:'Outfit'}}>WFH Blog & Guides</h3>
                <p className="text-xs text-[#1C2A24]/50">Interview tips, scam alerts, setup guides & more</p>
              </div>
              <ArrowRight size={14} className="text-[#E5E0D8] group-hover:text-[#047857] ml-auto mt-1 transition-colors" strokeWidth={1.5} />
            </Link>
            <Link to="/salary-calculator" className="group flex items-start gap-4 bg-white rounded-2xl border border-[#E5E0D8] p-6 hover:border-[#047857]/30 transition-all" data-testid="resources-salary-link">
              <div className="w-10 h-10 rounded-xl bg-[#D97757]/10 flex items-center justify-center shrink-0">
                <DollarSign size={18} className="text-[#D97757]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-sm font-medium mb-1" style={{fontFamily:'Outfit'}}>Salary Calculator</h3>
                <p className="text-xs text-[#1C2A24]/50">Compare WFH salaries by role & state</p>
              </div>
              <ArrowRight size={14} className="text-[#E5E0D8] group-hover:text-[#D97757] ml-auto mt-1 transition-colors" strokeWidth={1.5} />
            </Link>
            <Link to="/job-alerts" className="group flex items-start gap-4 bg-white rounded-2xl border border-[#E5E0D8] p-6 hover:border-[#047857]/30 transition-all" data-testid="resources-alerts-link">
              <div className="w-10 h-10 rounded-xl bg-[#86A789]/20 flex items-center justify-center shrink-0">
                <Bell size={18} className="text-[#86A789]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-sm font-medium mb-1" style={{fontFamily:'Outfit'}}>Job Alerts</h3>
                <p className="text-xs text-[#1C2A24]/50">Get new WFH jobs emailed to you weekly</p>
              </div>
              <ArrowRight size={14} className="text-[#E5E0D8] group-hover:text-[#86A789] ml-auto mt-1 transition-colors" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* Hire Fast – Post a Job Instantly */}
      <section className="py-24 md:py-32" data-testid="hire-fast-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl tracking-tight font-medium mb-4" style={{fontFamily:'Outfit'}}>Hire Fast — Post a Job Instantly</h2>
            <p className="text-base text-[#1C2A24]/60 max-w-2xl mx-auto">Need workers now? Post your job in minutes and get immediate visibility. No subscription required.</p>
          </div>

          {postSuccess && (
            <div className="max-w-lg mx-auto bg-[#A7F3D0]/30 border border-[#047857]/20 rounded-2xl p-6 mb-8 text-center" data-testid="post-success-banner">
              <CheckCircle2 size={24} className="text-[#047857] mx-auto mb-2" strokeWidth={1.5} />
              <p className="font-medium text-[#047857]" style={{fontFamily:'Outfit'}}>Job submitted successfully!</p>
              <p className="text-sm text-[#1C2A24]/60 mt-1">Your post is being reviewed and will go live shortly.</p>
            </div>
          )}

          {!showPostForm ? (
            <div className="text-center">
              <Button onClick={() => setShowPostForm(true)} className="rounded-full bg-[#D97757] hover:bg-[#C4623F] text-white h-12 px-8 text-base" data-testid="post-job-cta">
                <Send size={16} className="mr-2" strokeWidth={1.5} /> Post a Job — $29.99 One-Time
              </Button>
              <p className="text-xs text-[#1C2A24]/40 mt-3">All job posts are reviewed before going live.</p>
            </div>
          ) : (
            <div className="max-w-lg mx-auto bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="post-job-form">
              <h3 className="text-lg font-medium mb-5" style={{fontFamily:'Outfit'}}>Post Your Job</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-[#1C2A24]/60 mb-1 block">Job Title *</label>
                  <input value={postForm.title} onChange={e => setPostForm(f => ({...f, title: e.target.value}))} placeholder="e.g. Customer Service Representative"
                    className="w-full h-11 rounded-xl bg-[#F4F1E8]/50 border border-[#E5E0D8] px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#047857]" data-testid="post-title" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-[#1C2A24]/60 mb-1 block">Pay *</label>
                    <input value={postForm.pay} onChange={e => setPostForm(f => ({...f, pay: e.target.value}))} placeholder="e.g. $15-18/hr"
                      className="w-full h-11 rounded-xl bg-[#F4F1E8]/50 border border-[#E5E0D8] px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#047857]" data-testid="post-pay" />
                  </div>
                  <div>
                    <label className="text-xs text-[#1C2A24]/60 mb-1 block">Location</label>
                    <input value={postForm.location} onChange={e => setPostForm(f => ({...f, location: e.target.value}))} placeholder="Remote"
                      className="w-full h-11 rounded-xl bg-[#F4F1E8]/50 border border-[#E5E0D8] px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#047857]" data-testid="post-location" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#1C2A24]/60 mb-1 block">Short Job Description *</label>
                  <textarea value={postForm.description} onChange={e => setPostForm(f => ({...f, description: e.target.value}))} placeholder="Describe the role, requirements, and benefits..." rows={3}
                    className="w-full rounded-xl bg-[#F4F1E8]/50 border border-[#E5E0D8] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#047857] resize-none" data-testid="post-description" />
                </div>
                <div>
                  <label className="text-xs text-[#1C2A24]/60 mb-1 block">Contact Email *</label>
                  <input value={postForm.contact_email} onChange={e => setPostForm(f => ({...f, contact_email: e.target.value}))} type="email" placeholder="hiring@company.com"
                    className="w-full h-11 rounded-xl bg-[#F4F1E8]/50 border border-[#E5E0D8] px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#047857]" data-testid="post-email" />
                </div>
                <Button onClick={handlePostJob} disabled={posting || !postForm.title || !postForm.pay || !postForm.contact_email || !postForm.description}
                  className="w-full rounded-full bg-[#D97757] hover:bg-[#C4623F] text-white h-12 text-sm" data-testid="post-submit">
                  {posting ? <Loader2 size={16} className="animate-spin" /> : <><Send size={14} className="mr-2" /> Pay & Post Job — $29.99</>}
                </Button>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-[#1C2A24]/40">All job posts are reviewed before going live.</p>
                  <button onClick={() => setShowPostForm(false)} className="text-xs text-[#1C2A24]/40 hover:text-[#1C2A24]/60">Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Featured Jobs */}
          {featuredJobs.length > 0 && (
            <div className="mt-16" data-testid="featured-upfront-jobs">
              <h3 className="text-lg font-medium mb-6 text-center" style={{fontFamily:'Outfit'}}>Featured Jobs</h3>
              <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
                {featuredJobs.map(job => (
                  <div key={job.id} className="bg-white rounded-2xl border border-[#E5E0D8] p-6 shadow-[0_4px_16px_rgb(0,0,0,0.03)]" data-testid={`featured-job-${job.id}`}>
                    <h4 className="font-medium text-sm mb-2" style={{fontFamily:'Outfit'}}>{job.title}</h4>
                    <div className="flex items-center gap-3 mb-3 text-xs text-[#1C2A24]/60">
                      <span className="flex items-center gap-1"><DollarSign size={10} strokeWidth={1.5} />{job.pay}</span>
                      <span className="flex items-center gap-1"><MapPin size={10} strokeWidth={1.5} />{job.location}</span>
                    </div>
                    <p className="text-xs text-[#1C2A24]/50 leading-relaxed mb-3 line-clamp-2">{job.description}</p>
                    <a href={`mailto:${job.contact_email}`} className="text-xs text-[#047857] font-medium hover:underline">Apply via Email</a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Start Finding Jobs - Instant Value */}
      <section className="py-20 md:py-24 bg-[#E8F5E9]/20" data-testid="instant-value">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl tracking-tight font-medium mb-4" style={{fontFamily:'Outfit'}}>Start Finding Jobs in Seconds</h2>
          <p className="text-base text-[#1C2A24]/70 mb-8">No complicated steps. No confusing process. Just real jobs, ready for you.</p>
          <Link to="/jobs">
            <Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-12 px-10 text-base" data-testid="instant-value-btn">
              Start Searching Now <ArrowRight size={16} className="ml-2" strokeWidth={1.5} />
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA - Closing Push */}
      <section className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl tracking-tight font-medium mb-6" style={{fontFamily:'Outfit'}}>Stop Wasting Time. Start Getting Results.</h2>
          <p className="text-base text-[#1C2A24]/70 mb-8">Join others who are finding real work-from-home jobs without the frustration.</p>
          <Link to="/jobs">
            <Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-12 px-10 text-base" data-testid="cta-signup">
              Find Remote Jobs Now <ArrowRight size={16} className="ml-2" strokeWidth={1.5} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E5E0D8] py-12 bg-[#F4F1E8]/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-[#047857] flex items-center justify-center">
                <Briefcase size={10} className="text-white" strokeWidth={1.5} />
              </div>
              <span className="text-sm font-medium" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
            </div>
            <div className="flex items-center gap-6">
              <a href="https://www.tiktok.com/@workfromjoyhub" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-[#1C2A24]/60 hover:text-[#047857] transition-colors" data-testid="tiktok-footer-link">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0012.49-1.53V8.15a8.16 8.16 0 004.74 1.52v-3.4a4.85 4.85 0 01-.79.42z"/></svg>
                @workfromjoyhub
              </a>
              <Link to="/blog" className="text-sm text-[#1C2A24]/60 hover:text-[#047857]">Blog</Link>
              <Link to="/salary-calculator" className="text-sm text-[#1C2A24]/60 hover:text-[#047857]">Salary Calculator</Link>
              <Link to="/webinars" className="text-sm text-[#1C2A24]/60 hover:text-[#047857]">Webinars</Link>
              <Link to="/help-hub" className="text-sm text-[#1C2A24]/60 hover:text-[#047857]">Help Hub</Link>
              <Link to="/support" className="text-sm text-[#1C2A24]/60 hover:text-[#047857]">Support</Link>
              <Link to="/terms" className="text-sm text-[#1C2A24]/60 hover:text-[#047857]">Terms</Link>
              <Link to="/privacy" className="text-sm text-[#1C2A24]/60 hover:text-[#047857]">Privacy</Link>
            </div>
            <p className="text-sm text-[#1C2A24]/50">2026 WorkFromJoy. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
